import React from "react";
import type { Source } from "../../../../api/useSources";
import type { Project } from "../../../../api/useProjects";
import { usePeriodState, type Period } from "@ui/Highcharts/hooks/usePeriod";
import { parseAbsolute, toCalendarDateTime } from "@internationalized/date";
import { useProjectPeriod } from "../../useProjectPeriod";
import type { Event } from "../../../../api/useEvents";
import { getEventEndsAt, getEventScope } from "../../../../domain/event";
import type { ActiveAnalysis } from "../../../../api/useActiveAnalysesStream";

function getInitialPeriod({
  eventPeriod,
  timezone,
  extremes,
}: {
  eventPeriod: { from: string; to: string };
  timezone: string;
  extremes: Period;
}): Period {
  return {
    from: Math.max(
      extremes.from,
      toCalendarDateTime(parseAbsolute(new Date(eventPeriod.from).toISOString(), timezone))
        .add({ days: -2 })
        .toDate(timezone)
        .getTime(),
    ),
    to: Math.min(
      extremes.to,
      toCalendarDateTime(parseAbsolute(new Date(eventPeriod.to).toISOString(), timezone))
        .add({ days: 2 })
        .toDate(timezone)
        .getTime(),
    ),
  };
}

interface EventPeriod {
  event: Event;
  source: Source;
  project: Project;
}
interface EventPeriodWithAnalyses {
  analyses?: never;
  boundaries: "analyses";
}
interface EventPeriodWithActiveAnalyses {
  analyses: ActiveAnalysis[];
  boundaries: "active-analyses";
}
type Props = EventPeriod & (EventPeriodWithAnalyses | EventPeriodWithActiveAnalyses);

export function useEventPeriod({ event, source, project, analyses, boundaries }: Props) {
  const [, , projectExtremes] = useProjectPeriod(
    boundaries === "active-analyses"
      ? { source, project, boundaries, analyses }
      : { source, project, boundaries },
  );

  const eventStartsAt = new Date(event.startsAt).getTime();
  const eventEndsAt = new Date(getEventEndsAt(event)).getTime();
  const eventScope = getEventScope(event);
  const projectExtremesFrom = projectExtremes.from;
  const projectExtremesTo = projectExtremes.to;
  const eventPeriod = React.useMemo(
    () =>
      getInitialPeriod({
        eventPeriod: {
          from: new Date(eventStartsAt).toISOString(),
          to: new Date(eventEndsAt).toISOString(),
        },
        timezone: project.timezone,
        extremes: { from: projectExtremesFrom, to: projectExtremesTo },
      }),
    [eventStartsAt, eventEndsAt, project.timezone, projectExtremesFrom, projectExtremesTo],
  );

  const [period, setPeriod] = usePeriodState({
    scope: eventScope,
    defaultValue: eventPeriod,
  });

  const currentPeriodIsDefaultPeriod =
    period.from === eventPeriod.from && period.to === eventPeriod.to;

  const resetPeriod = React.useCallback(() => setPeriod(eventPeriod), [setPeriod, eventPeriod]);

  return React.useMemo(
    () =>
      [
        period,
        setPeriod,
        projectExtremes,
        currentPeriodIsDefaultPeriod,
        resetPeriod,
        eventPeriod,
      ] as const,
    [period, setPeriod, projectExtremes, currentPeriodIsDefaultPeriod, resetPeriod, eventPeriod],
  );
}
